import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumClaseFactura, EnumIdioma, EnumTipoFactura } from '../../../api'
import { EnumSubTipoConfiguracion } from '../../../api/oData/enums/enumSubTipoConfiguracion'
import { EnumTipoConfiguracion } from '../../../api/oData/enums/enumTipoConfiguracion'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { NonAuthRoutes } from '../../../model'
import { getCustomerDomain } from '../../../redux'
import TemplateSettings from '../templateSettings.model'
import {
  fetchClientListItems,
  fetchContractTypeListItems,
  updateTemplateSettings
} from '../templateSettings.service'

export interface TemplateSettingsEditPageProps {
  location?: {
    state: {
      productsList: SelectOption[]
      templateSettingsSelected: TemplateSettings
    }
  }
}

export const TemplateSettingsEditPage: React.FC<TemplateSettingsEditPageProps> = ({ location }) => {
  const history = useHistory()
  const [t] = useTranslation(['globalerisLibrary', 'templateSettings', 'enums'])
  const { info, error } = useNotify()

  const templateType: EnumTipoConfiguracion =
    location?.state?.templateSettingsSelected.templateType! || EnumTipoConfiguracion.Plantilla

  const productsList: SelectOption[] = location?.state?.productsList || []

  const initialFormData: TemplateSettings | undefined = location?.state?.templateSettingsSelected

  const [dataForm, setDataForm] = useState<TemplateSettings | undefined>(initialFormData)
  const [contractTypeList, setContractTypeList] = useState<SelectOption[]>([])
  const [clientList, setClientList] = useState<SelectOption[]>([])

  //#region useEffect

  useEffect(() => {
    const getContractTypeSelectOptionList = async () => {
      await fetchContractTypeListItems().then(response => {
        if (response.success && response.data) {
          setContractTypeList(response.data)
        } else {
          error(t('templateSettings:notifications.contractTypeListError'))
        }
      })
    }

    const getClientSelectOptionList = async () => {
      await fetchClientListItems().then(response => {
        if (response.success && response.data) {
          setClientList(response.data)
        } else {
          error(t('templateSettings:notifications.clientListError'))
        }
      })
    }

    getContractTypeSelectOptionList()
    getClientSelectOptionList()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //#endregion

  //#region Form and Datatable Definitions
  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'id',
          display: false
        },
        {
          name: 'templateType',
          label: t('templateSettings:dataTable.templateType'),
          readOnly: true,
          type: DataType.SELECT,
          required: true,
          selectOptions: enumToSelectOptionArray(
            EnumTipoConfiguracion,
            'enums:EnumTipoConfiguracion'
          )
        },
        {
          name: 'templateSubType',
          label: t('templateSettings:dataTable.templateSubType'),
          readOnly: true,
          type: DataType.SELECT,
          display: templateType === EnumTipoConfiguracion.Widget,
          required: templateType === EnumTipoConfiguracion.Widget,
          selectOptions: enumToSelectOptionArray(
            EnumSubTipoConfiguracion,
            'enums:EnumSubTipoConfiguracion'
          )
        },
        {
          name: 'description',
          label: t('templateSettings:dataTable.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'products',
          label: t('templateSettings:dataTable.products'),
          type: DataType.MULTISELECT,
          selectOptions: productsList
        },
        {
          name: 'contractType',
          label: t('templateSettings:dataTable.contractType'),
          type: DataType.MULTISELECT,
          selectOptions: contractTypeList
        },
        {
          name: 'client',
          label: t('templateSettings:dataTable.client'),
          type: DataType.MULTISELECT,
          selectOptions: clientList
        },
        {
          name: 'language',
          label: t('templateSettings:dataTable.language'),
          readOnly: true,
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumIdioma, 'enums:EnumIdioma')
        },
        {
          name: 'invoiceType',
          label: t('templateSettings:dataTable.invoiceType'),
          type: DataType.SELECT,
          clearable: true,
          selectOptions: enumToSelectOptionArray(EnumTipoFactura, 'enums:EnumTipoFactura')
        },
        {
          name: 'invoiceClass',
          label: t('templateSettings:dataTable.invoiceClass'),
          readOnly: false,
          type: DataType.SELECT,
          clearable: true,
          selectOptions: enumToSelectOptionArray(EnumClaseFactura, 'enums:EnumClaseFactura')
        },
        {
          name: 'currentVersion',
          label: t('templateSettings:dataTable.currentVersion'),
          type: DataType.TEXT,
          readOnly: true
        }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('templateSettings:edit.template')
    }
  }
  //#endregion

  //#region Handler
  const updateTemplateHandler = async () => {
    if (dataForm && !missingFieldsFormWithRequiredText(formSections, dataForm)) {
      await updateTemplateSettings(dataForm, productsList, contractTypeList, clientList).then(
        response => {
          if (response.success) {
            info(t('templateSettings:forms.edit.notifications.save.success'))
            history.goBack()
          } else {
            error(t('templateSettings:forms.edit.notifications.save.error'))
          }
        }
      )
    } else {
      error(t('templateSettings:forms.edit.notifications.incompleteForm'))
    }
  }

  //#endregion
  return (
    <Page>
      <PageHeader title={t('templateSettings:edit.title')}>
        <Grid container direction="row" justify="flex-end" spacing={2}>
          <Grid item>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => {
                history.push(NonAuthRoutes.TemplateSettings)
              }}
            >
              {t('templateSettings:button.back')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={updateTemplateHandler}
            >
              {t('templateSettings:button.save')}
            </Button>
          </Grid>
        </Grid>
      </PageHeader>
      <Form sections={formSections} data={dataForm} options={formOptions} />
    </Page>
  )
}
